import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { t } from 'i18next';
import { MathUtils, Vector2 } from 'three';

import { Accordion } from '@/modules/common/components/Accordion';
import { Dimensions } from '@/modules/common/components/Dimensions';
import { clampAngle180 } from '@/modules/common/helpers/math';
import { NumUtils } from '@/modules/common/utils';
import { computeRectanglesCenterBoundingBox } from '@/modules/common/helpers/boundingBox';
import { CenteredOrientedBoundingBox } from '@/modules/common/types/boundingBox';
import { offsetSegments } from '@/modules/floorplanDesign/helpers/helpers';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { Panel_Display_Mode } from '@/modules/floorplanDesign/types';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

type SegmentDimensionsMode = 'highway' | 'wall';

type SegmentDimensionsProps = {
  mode: SegmentDimensionsMode;
};

type FloorplanDesignState = ReturnType<typeof useFloorplanDesignStore.getState>;

const getSelectedSegmentBounds = (
  mode: SegmentDimensionsMode,
  selectedIds: string[],
  floorplanState: FloorplanDesignState,
): CenteredOrientedBoundingBox | null => {
  const selectedItems =
    mode === 'highway'
      ? floorplanState.getAreasByIds(selectedIds).highwayAreas.map((area) => area.segments)
      : floorplanState.getObstaclesByIds(selectedIds).map((obstacle) => obstacle.segments); 

  if (selectedItems.length === 0) return null;
  if (selectedItems.length === 1 && selectedItems[0].length === 1) return null;

  const rectangles = selectedItems.flatMap((segments) =>
    segments.flatMap(RectangleUtils.fromSegment),
  );

  if (rectangles.length === 0) {
    return null;
  }

  const box = computeRectanglesCenterBoundingBox(rectangles);
  return {...box, angle: 0};
};

const useSelectedSegmentDimension = (
  mode: SegmentDimensionsMode,
  selectedIds: string[],
  value: keyof CenteredOrientedBoundingBox,
): number | null =>
  useFloorplanDesignStore((state) => {
    const bounds = getSelectedSegmentBounds(mode, selectedIds, state);
    return bounds ? bounds[value] : null;
  });

const useSingleSegmentEditable = (mode: SegmentDimensionsMode, selectedIds: string[]): boolean =>
  useFloorplanDesignStore((state) => {
    if (mode === 'highway') {
      const selectedHighways = state.getAreasByIds(selectedIds).highwayAreas;
      return selectedHighways.length === 1 && selectedHighways[0].segments.length === 1;
    }

    const selectedWalls = state.getObstaclesByIds(selectedIds)
    return selectedWalls.length === 1 && selectedWalls[0].segments.length === 1;
  });

const useSegmentDimensions = (mode: SegmentDimensionsMode) => {
  const panelModeState = useAppStore((state) => state.panelModeState);
  const selectedIds = useAppStore((state) => state.selectedIds);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const centerX = useSelectedSegmentDimension(mode, selectedIds, 'centerX');
  const centerY = useSelectedSegmentDimension(mode, selectedIds, 'centerY');
  const height = useSelectedSegmentDimension(mode, selectedIds, 'height');
  const width = useSelectedSegmentDimension(mode, selectedIds, 'width');
  const angle = useSelectedSegmentDimension(mode, selectedIds, 'angle');

  const dimensions = useMemo(() => {
    if (centerX === null || centerY === null || height === null || width === null || angle === null) {
      return null;
    }

    return { centerX, centerY, height, width, angle };
  }, [height, angle, width, centerX, centerY]);

  const currentDimensionsRef = useRef<CenteredOrientedBoundingBox | null>(null);

  useEffect(() => {
    currentDimensionsRef.current = dimensions;
  }, [dimensions]);

  const getHighwaySelection = useCallback(() => {
    const floorplanState = useFloorplanDesignStore.getState();
    return floorplanState.getAreasByIds(selectedIds).highwayAreas;
  }, [selectedIds]);

  const getWallSelection = useCallback(() => {
    const floorplanState = useFloorplanDesignStore.getState();
    return floorplanState.getObstaclesByIds(selectedIds) // .filter((obstacle) => obstacle.appearance === 'WALL');
  }, [selectedIds]);

  const onXChange = useCallback(
    (newValue: number) => {
      if (!currentDimensionsRef.current) return;

      const deltaX = NumUtils.mmToMetersRoundedToMm(newValue) - currentDimensionsRef.current.centerX;
      if (deltaX === 0) return;

      if (mode === 'highway') {
        const selectedAreas = getHighwaySelection();
        selectedAreas.forEach((area) => {
          fddmSvc.updateArea({
            ...area,
            segments: offsetSegments(area.segments, new Vector2(deltaX, 0)),
          });
        });
        return;
      }

      getWallSelection().forEach((obstacle) => {
        fddmSvc.updateObstacle({
          ...obstacle,
          segments: offsetSegments(obstacle.segments, new Vector2(deltaX, 0)),
        });
      });
    },
    [fddmSvc, getHighwaySelection, getWallSelection, mode],
  );

  const onYChange = useCallback(
    (newValue: number) => {
      if (!currentDimensionsRef.current) return;

      const deltaY = NumUtils.mmToMetersRoundedToMm(newValue) - currentDimensionsRef.current.centerY;
      if (deltaY === 0) return;

      if (mode === 'highway') {
        getHighwaySelection().forEach((area) => {
          fddmSvc.updateArea({
            ...area,
            segments: offsetSegments(area.segments, new Vector2(0, deltaY)),
          });
        });
        return;
      }

      getWallSelection().forEach((obstacle) => {
        fddmSvc.updateObstacle({
          ...obstacle,
          segments: offsetSegments(obstacle.segments, new Vector2(0, deltaY)),
        });
      });
    },
    [fddmSvc, getHighwaySelection, getWallSelection, mode],
  );

  const onLengthChange = useCallback(
    (newValue: number) => {
      const newLength = NumUtils.mmToMetersRoundedToMm(newValue);
      if (newLength <= 0) return;

      if (mode === 'highway') {
        const selectedHighway = getHighwaySelection()[0];

        const segment = selectedHighway?.segments[0];
        if (!selectedHighway || !segment) return;

        const rectangle = RectangleUtils.fromSegment(segment);
        const updatedSegment = RectangleUtils.toPartialSegment({
          ...rectangle,
          length: newLength,
        });

        fddmSvc.updateArea({
          ...selectedHighway,
          segments: minItemsArrayUtils.mapAtLeast1(selectedHighway.segments, (item, index) =>
            index === 0 ? { ...item, ...updatedSegment } : item,
          ),
        });
        return;
      }

      const selectedWall = getWallSelection()[0];

      const segment = selectedWall?.segments[0];
      if (!selectedWall || !segment) return;

      const rectangle = RectangleUtils.fromSegment(segment);
      const updatedSegment = RectangleUtils.toPartialSegment({
        ...rectangle,
        length: newLength,
      });

      fddmSvc.updateObstacle({
        ...selectedWall,
        segments: minItemsArrayUtils.mapAtLeast1(selectedWall.segments, (item, index) =>
          index === 0 ? { ...item, ...updatedSegment } : item,
        ),
      });
    },
    [fddmSvc, getHighwaySelection, getWallSelection, mode],
  );

  const onWidthChange = useCallback(
    (newValue: number) => {
      const newThickness = NumUtils.mmToMetersRoundedToMm(newValue);
      if (newThickness <= 0) return;

      if (mode === 'highway') {
        const selectedHighway = getHighwaySelection()[0];

        const segment = selectedHighway?.segments[0];
        if (!selectedHighway || !segment) return;

        const rectangle = RectangleUtils.fromSegment(segment);
        const updatedSegment = RectangleUtils.toPartialSegment({
          ...rectangle,
          width: newThickness,
        });

        fddmSvc.updateArea({
          ...selectedHighway,
          segments: minItemsArrayUtils.mapAtLeast1(selectedHighway.segments, (item, index) =>
            index === 0 ? { ...item, ...updatedSegment } : item,
          ),
        });
        return;
      }

      const selectedWall = getWallSelection()[0];

      const segment = selectedWall?.segments[0];
      if (!selectedWall || !segment) return;

      const rectangle = RectangleUtils.fromSegment(segment);
      const updatedSegment = RectangleUtils.toPartialSegment({
        ...rectangle,
        width: newThickness,
      });

      fddmSvc.updateObstacle({
        ...selectedWall,
        segments: minItemsArrayUtils.mapAtLeast1(selectedWall.segments, (item, index) =>
          index === 0 ? { ...item, ...updatedSegment } : item,
        ),
      });
    },
    [fddmSvc, getHighwaySelection, getWallSelection, mode],
  );

  const onAngleChange = useCallback(
    (newValue: number) => {
      // Segment bounding boxes use an inverted UI angle sign compared to segment geometry.
      const newAngle = MathUtils.degToRad(clampAngle180(newValue));

      if (mode === 'highway') {
        const selectedHighway = getHighwaySelection()[0];

        const segment = selectedHighway?.segments[0];
        if (!selectedHighway || !segment) return;

        const rectangle = RectangleUtils.fromSegment(segment);
        const updatedSegment = RectangleUtils.toPartialSegment({
          ...rectangle,
          angle: newAngle,
        });

        fddmSvc.updateArea({
          ...selectedHighway,
          segments: minItemsArrayUtils.mapAtLeast1(selectedHighway.segments, (item, index) =>
            index === 0 ? { ...item, ...updatedSegment } : item,
          ),
        });
        return;
      }

      const selectedWall = getWallSelection()[0];

      const segment = selectedWall?.segments[0];
      if (!selectedWall || !segment) return;

      const rectangle = RectangleUtils.fromSegment(segment);
      const updatedSegment = RectangleUtils.toPartialSegment({
        ...rectangle,
        angle: newAngle,
      });

      fddmSvc.updateObstacle({
        ...selectedWall,
        segments: minItemsArrayUtils.mapAtLeast1(selectedWall.segments, (item, index) =>
          index === 0 ? { ...item, ...updatedSegment } : item,
        ),
      });
    },

    [fddmSvc, getHighwaySelection, getWallSelection, mode],
  );

  return {
    dimensions,
    onXChange,
    onYChange,
    onLengthChange,
    onWidthChange,
    onAngleChange,
    panelModeState,
    selectedIds,
  };
};

function SegmentDimensionsComponent({ mode }: SegmentDimensionsProps) {
  const {
    dimensions,
    onXChange,
    onYChange,
    onLengthChange,
    onWidthChange,
    onAngleChange,
    panelModeState,
    selectedIds,
  } = useSegmentDimensions(mode);
  
  const appMode = useAppStore((state) => state.mode);
  
  const isReadOnly = appMode !== 'EDITABLE';

  const templateModeDisabled =
    panelModeState === Panel_Display_Mode.TEMPLATE ||
    panelModeState === Panel_Display_Mode.TEMPLATE_LASSIE ||
    panelModeState === Panel_Display_Mode.TEMPLATE_LASSIE_MINI ||
    panelModeState === Panel_Display_Mode.TEMPLATE_SHUTTLE_RACK ||
    panelModeState === Panel_Display_Mode.TEMPLATE_HIK;
  
  const disabled = isReadOnly || templateModeDisabled;
    
  const isGeometryEditable = useSingleSegmentEditable(mode, selectedIds);
  
  if (!dimensions) return null;

  return (
    <Accordion
      title={t<string>('interface:properties.dimensions.label', 'Dimensions')}
      unmountOnExit
      defaultExpanded
      sx={{ px: 2 }}
    >
      <Dimensions
        disabled={disabled}
        disableLength={!isGeometryEditable}
        disableWidth={!isGeometryEditable}
        disableAngle={!isGeometryEditable}
        x={NumUtils.metersToMmRounded(dimensions.centerX)}
        onXChange={onXChange}
        y={NumUtils.metersToMmRounded(dimensions.centerY)}
        onYChange={onYChange}
        length={NumUtils.metersToMmRounded(dimensions.width)}
        onLengthChange={onLengthChange}
        width={NumUtils.metersToMmRounded(dimensions.height)}
        onWidthChange={onWidthChange}
        angle={dimensions.angle}
        onAngleChange={onAngleChange}
      />
    </Accordion>
  );
}

export const SegmentDimensions = memo(SegmentDimensionsComponent);
